import { useState, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Ruler, Send, Trash2, Plus, Package, FileText } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Balustrade dimensions (mm)
const BALUSTRADE_WIDTH = 155; // 75mm each side + 5mm glass

// Product data with accurate dimensions (widths in mm) from ZKTeco specs
const PRODUCTS = [
  // Speedlanes (Comet) - NO balustrade needed between these
  { 
    id: 'comet-s1000-std', 
    name: 'Comet S1000 Standard', 
    category: 'speedlane',
    width: 1022, // mm - accurate measurement
    orderCode: 'ACC-BASE-COMET-STD-TIS-UK',
    description: 'Standard Speedlane Base',
    color: '#3B82F6', // blue
    needsBalustrade: false,
    image: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/2cgmzmkn_Comet.png',
    datasheet: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/z30e27yp_Comet%20Datasheet%20%282%29.pdf'
  },
  { 
    id: 'comet-s1000-dda', 
    name: 'Comet S1000 DDA', 
    category: 'speedlane',
    width: 1282, // mm - accurate measurement
    orderCode: 'ACC-BASE-COMET-A-UK',
    description: 'DDA/Accessible Speedlane Base',
    color: '#3B82F6',
    needsBalustrade: false,
    image: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/2cgmzmkn_Comet.png',
    datasheet: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/z30e27yp_Comet%20Datasheet%20%282%29.pdf'
  },
  // Half Height Turnstiles - balustrade needed between lanes
  { 
    id: 'ts1000', 
    name: 'TS1000 Pro', 
    category: 'half_height',
    width: 810, // mm - footprint width from datasheet (520x810mm)
    orderCode: 'TS1000',
    description: 'Tripod Turnstile - Compact',
    color: '#10B981', // green
    needsBalustrade: true,
    image: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/wo5ptzys_TS1000Pro.jpg',
    datasheet: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/entkewdy_TS1000%20pro-Leaflet-202112%20%281%29.pdf'
  },
  { 
    id: 'ts2000', 
    name: 'TS2000 Pro', 
    category: 'half_height',
    width: 1110, // mm - footprint width from datasheet (785x1110mm)
    orderCode: 'TS2000',
    description: 'Tripod Turnstile - Bridge Style',
    color: '#10B981',
    needsBalustrade: true,
    image: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/v1rhgjoc_TS2000Pro.png',
    datasheet: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/dgm1dcfv_TS2000%20pro-Leaflet-202112%20%281%29.pdf'
  },
  // Swing Barrier Gates - balustrade needed between lanes
  { 
    id: 'sbt3000s', 
    name: 'SBT3000S (Single)', 
    category: 'swing_barrier',
    width: 1040, // mm - accurate measurement
    orderCode: 'SBT3000S',
    description: 'Single Swing Barrier',
    color: '#8B5CF6', // purple
    needsBalustrade: true,
    image: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/rx0wmog6_SBT3000S.jpg',
    datasheet: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/meitc6sg_sbt3000.pdf'
  },
  { 
    id: 'sbt3000d', 
    name: 'SBT3000D (Double)', 
    category: 'swing_barrier',
    width: 1610, // mm - accurate measurement
    orderCode: 'SBT3000D',
    description: 'Double Swing Barrier',
    color: '#8B5CF6',
    needsBalustrade: true,
    image: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/ta7oqknd_SBT3000D.jpg',
    datasheet: 'https://customer-assets.emergentagent.com/job_d3754c58-11de-4206-b173-a8639e69b673/artifacts/meitc6sg_sbt3000.pdf'
  },
];

const CATEGORIES = [
  { id: 'speedlane', name: 'Comet Speedlane', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  { id: 'half_height', name: 'Half Height Turnstile', color: 'bg-green-100 text-green-700 border-green-300' },
  { id: 'swing_barrier', name: 'Swing Barrier', color: 'bg-purple-100 text-purple-700 border-purple-300' },
];

const AmazonHardeningProject = ({ onBack }) => {
  const [siteCode, setSiteCode] = useState("");
  const [totalWidth, setTotalWidth] = useState("");
  const [requiresBases, setRequiresBases] = useState("");
  const [selectedCategories, setSelectedCategories] = useState(['speedlane', 'half_height', 'access_gate']);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [sending, setSending] = useState(false);

  // Calculate balustrades needed (for non-Comet products)
  // If there are N non-Comet products in a row, we need N-1 balustrades between them
  // Plus 2 balustrades on the outer edges if there are any non-Comet products
  const calculateBalustrades = (products) => {
    // Group consecutive non-Comet products
    let nonCometCount = products.filter(p => p.needsBalustrade).length;
    
    if (nonCometCount === 0) return 0;
    
    // For non-Comet products: need balustrades between them + 2 on the outside
    // If 1 product: 2 balustrades (one on each side)
    // If 2 products: 3 balustrades (sides + middle)
    // If 3 products: 4 balustrades (sides + 2 middles)
    // Formula: nonCometCount + 1 balustrades
    return nonCometCount + 1;
  };

  const balustradesNeeded = calculateBalustrades(selectedProducts);
  const balustradeWidth = balustradesNeeded * BALUSTRADE_WIDTH;

  // Calculate remaining width (including balustrade space)
  const productWidth = selectedProducts.reduce((sum, p) => sum + p.width, 0);
  const usedWidth = productWidth + balustradeWidth;
  const remainingWidth = (parseInt(totalWidth) || 0) - usedWidth;

  // Filter products by category and remaining width (account for potential balustrade)
  const availableProducts = PRODUCTS.filter(p => {
    const categoryMatch = selectedCategories.includes(p.category);
    // If adding a non-Comet product, account for the additional balustrade
    const additionalBalustrade = p.needsBalustrade ? BALUSTRADE_WIDTH : 0;
    const fitsInSpace = (p.width + additionalBalustrade) <= remainingWidth;
    return categoryMatch && fitsInSpace;
  });

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(c => c !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleAddProduct = (product) => {
    if (product.width <= remainingWidth) {
      setSelectedProducts(prev => [...prev, { ...product, instanceId: Date.now() }]);
    }
  };

  const handleRemoveProduct = (instanceId) => {
    setSelectedProducts(prev => prev.filter(p => p.instanceId !== instanceId));
  };

  const handleClearSelection = () => {
    setSelectedProducts([]);
  };

  const handleSendToZKTeco = async () => {
    setError("");
    setSuccess("");

    if (!siteCode.trim()) {
      setError("Please enter a site code");
      return;
    }
    if (!totalWidth || parseInt(totalWidth) <= 0) {
      setError("Please enter a valid total width");
      return;
    }
    if (selectedProducts.length === 0) {
      setError("Please select at least one product");
      return;
    }

    setSending(true);

    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      await axios.post(`${API}/amazon/send-configuration`, {
        site_code: siteCode,
        total_width: parseInt(totalWidth),
        product_width: productWidth,
        balustrade_count: balustradesNeeded,
        balustrade_width: balustradeWidth,
        used_width: usedWidth,
        remaining_width: remainingWidth,
        requires_bases: requiresBases === 'yes',
        products: selectedProducts.map(p => ({
          id: p.id,
          name: p.name,
          category: p.category,
          width: p.width,
          orderCode: p.orderCode,
          needsBalustrade: p.needsBalustrade
        })),
        user_email: user.email,
        user_name: `${user.first_name || ''} ${user.last_name || ''}`.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess("Configuration sent to ZKTeco for pricing! You will receive a response shortly.");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send configuration");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-100 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={onBack}
          className="mb-6 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
              <Ruler className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Hardening Project</h1>
          <p className="text-slate-600">Internal Entrance Control Configurator</p>
        </div>

        {/* Messages */}
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="mb-6 bg-green-50 border-green-200">
            <AlertDescription className="text-green-700">{success}</AlertDescription>
          </Alert>
        )}

        {/* Site Details */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Site Configuration</CardTitle>
            <CardDescription>Enter your site details and available space</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="site-code">Site Code *</Label>
                <Input
                  id="site-code"
                  placeholder="e.g., BHX1"
                  value={siteCode}
                  onChange={(e) => setSiteCode(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="total-width">Total Available Width (mm) *</Label>
                <Input
                  id="total-width"
                  type="number"
                  placeholder="e.g., 5000"
                  value={totalWidth}
                  onChange={(e) => setTotalWidth(e.target.value)}
                />
              </div>
            </div>
            
            {/* Bases Question - Show after width is entered */}
            {totalWidth && parseInt(totalWidth) > 0 && (
              <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <Label className="text-sm font-medium mb-2 block">Do you require Bases?</Label>
                <p className="text-xs text-slate-600 mb-3">
                  All cables can be routed through the base to avoid civil requirements with the flooring.
                </p>
                <div className="flex gap-4">
                  <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                    requiresBases === 'yes' 
                      ? 'bg-orange-100 border-orange-400 text-orange-700' 
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}>
                    <input
                      type="radio"
                      name="requires-bases"
                      value="yes"
                      checked={requiresBases === 'yes'}
                      onChange={() => setRequiresBases('yes')}
                      className="sr-only"
                    />
                    <span className="font-medium">Yes</span>
                  </label>
                  <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                    requiresBases === 'no' 
                      ? 'bg-orange-100 border-orange-400 text-orange-700' 
                      : 'bg-white border-slate-200 hover:border-slate-300'
                  }`}>
                    <input
                      type="radio"
                      name="requires-bases"
                      value="no"
                      checked={requiresBases === 'no'}
                      onChange={() => setRequiresBases('no')}
                      className="sr-only"
                    />
                    <span className="font-medium">No</span>
                  </label>
                </div>
              </div>
            )}
            
            {/* Width Progress Bar */}
            {totalWidth && parseInt(totalWidth) > 0 && (
              <div className="mt-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">
                    Space Used: {usedWidth}mm 
                    <span className="text-slate-400 ml-1">
                      (Products: {productWidth}mm{balustradesNeeded > 0 ? ` + Balustrades: ${balustradeWidth}mm` : ''})
                    </span>
                  </span>
                  <span className={`font-medium ${remainingWidth < 0 ? 'text-red-600' : 'text-green-600'}`}>
                    Remaining: {remainingWidth}mm
                  </span>
                </div>
                <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-300 ${remainingWidth < 0 ? 'bg-red-500' : 'bg-green-500'}`}
                    style={{ width: `${Math.min((usedWidth / parseInt(totalWidth)) * 100, 100)}%` }}
                  />
                </div>
                {balustradesNeeded > 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    ℹ️ {balustradesNeeded} balustrade(s) required ({BALUSTRADE_WIDTH}mm each) for non-Comet products
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Main Content - Two Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Product Selection */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="h-5 w-5" />
                Product Selection
              </CardTitle>
              <CardDescription>Filter and select products that fit your space</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Category Filter */}
              <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                <Label className="text-sm font-medium mb-2 block">Filter by Category</Label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map(cat => (
                    <label 
                      key={cat.id}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border cursor-pointer transition-all ${
                        selectedCategories.includes(cat.id) 
                          ? cat.color 
                          : 'bg-white text-slate-400 border-slate-200'
                      }`}
                    >
                      <Checkbox
                        checked={selectedCategories.includes(cat.id)}
                        onCheckedChange={() => handleCategoryToggle(cat.id)}
                        className="h-4 w-4"
                      />
                      <span className="text-sm font-medium">{cat.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Available Products */}
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {!totalWidth || parseInt(totalWidth) <= 0 ? (
                  <p className="text-slate-500 text-center py-8">
                    Enter a total width to see available products
                  </p>
                ) : availableProducts.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">
                    {remainingWidth <= 0 
                      ? "No more space available" 
                      : "No products fit in the remaining space"}
                  </p>
                ) : (
                  availableProducts.map(product => (
                    <div 
                      key={product.id}
                      className="flex items-center justify-between p-3 bg-white border rounded-lg hover:border-orange-300 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {/* Product Image */}
                        <div className="w-16 h-16 rounded bg-slate-100 flex items-center justify-center overflow-hidden border">
                          {product.image ? (
                            <img 
                              src={product.image} 
                              alt={product.name}
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className={`w-full h-full flex items-center justify-center text-white text-xs font-bold ${!product.image ? '' : 'hidden'}`}
                            style={{ backgroundColor: product.color }}
                          >
                            {product.width}mm
                          </div>
                        </div>
                        <div>
                          <p className="font-medium text-sm">{product.name}</p>
                          <p className="text-xs text-slate-500">{product.width}mm wide</p>
                          <div className="flex gap-2 mt-1">
                            {product.datasheet && (
                              <a 
                                href={product.datasheet}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FileText className="h-3 w-3" />
                                Datasheet
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAddProduct(product)}
                        className="text-orange-600 border-orange-300 hover:bg-orange-50"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Right Column - Visual Preview */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Ruler className="h-5 w-5" />
                Visual Preview
              </CardTitle>
              <CardDescription>See how your selection looks (to scale)</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Visual Area */}
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 bg-slate-50 min-h-[200px]">
                {selectedProducts.length === 0 ? (
                  <div className="flex items-center justify-center h-[180px] text-slate-400">
                    <p>Select products to see preview</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Visual representation with balustrades */}
                    <div className="relative h-32 bg-slate-200 rounded-lg overflow-hidden flex">
                      {selectedProducts.map((product, index) => {
                        const isFirstNonComet = index === 0 && product.needsBalustrade;
                        const showBalustradeBefore = product.needsBalustrade && (index === 0 || selectedProducts[index - 1]?.needsBalustrade);
                        const showBalustradeAfter = product.needsBalustrade && index === selectedProducts.length - 1;
                        
                        return (
                          <div key={product.instanceId} className="flex h-full">
                            {/* Balustrade before (for first non-Comet product or between non-Comet products) */}
                            {showBalustradeBefore && (
                              <div
                                className="h-full bg-amber-400 flex items-center justify-center text-amber-900 text-[9px] font-bold border-r border-white/50"
                                style={{ 
                                  width: totalWidth ? `${(BALUSTRADE_WIDTH / parseInt(totalWidth)) * 100}%` : '10px',
                                  minWidth: '8px'
                                }}
                                title={`Balustrade (${BALUSTRADE_WIDTH}mm)`}
                              >
                                <span className="rotate-90 whitespace-nowrap">B</span>
                              </div>
                            )}
                            
                            {/* Product */}
                            <div
                              className="h-full flex flex-col items-center justify-center text-white text-xs font-medium border-r border-white/30 relative group"
                              style={{ 
                                backgroundColor: product.color,
                                width: totalWidth ? `${(product.width / parseInt(totalWidth)) * 100}%` : 'auto',
                                minWidth: '50px'
                              }}
                              title={`${product.name} (${product.width}mm)`}
                            >
                              <span className="truncate px-1 text-[10px]">{product.name.split(' ')[0]}</span>
                              <span className="text-[9px] opacity-80">{product.width}mm</span>
                              <button
                                onClick={() => handleRemoveProduct(product.instanceId)}
                                className="absolute top-1 right-1 w-5 h-5 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                            
                            {/* Balustrade after (for last non-Comet product) */}
                            {showBalustradeAfter && (
                              <div
                                className="h-full bg-amber-400 flex items-center justify-center text-amber-900 text-[9px] font-bold border-r border-white/50"
                                style={{ 
                                  width: totalWidth ? `${(BALUSTRADE_WIDTH / parseInt(totalWidth)) * 100}%` : '10px',
                                  minWidth: '8px'
                                }}
                                title={`Balustrade (${BALUSTRADE_WIDTH}mm)`}
                              >
                                <span className="rotate-90 whitespace-nowrap">B</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* Remaining space */}
                      {remainingWidth > 0 && totalWidth && (
                        <div 
                          className="h-full bg-slate-300 flex items-center justify-center text-slate-500 text-xs"
                          style={{ width: `${(remainingWidth / parseInt(totalWidth)) * 100}%` }}
                        >
                          {remainingWidth}mm
                        </div>
                      )}
                    </div>

                    {/* Measurements bar */}
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <div className="flex-1 border-t-2 border-slate-400 relative">
                        <span className="absolute -top-3 left-0 bg-slate-50 px-1">{totalWidth}mm total</span>
                      </div>
                    </div>

                    {/* Legend */}
                    <div className="flex flex-wrap gap-2">
                      {CATEGORIES.filter(cat => selectedProducts.some(p => p.category === cat.id)).map(cat => (
                        <span key={cat.id} className={`text-xs px-2 py-1 rounded ${cat.color}`}>
                          {cat.name}
                        </span>
                      ))}
                      {balustradesNeeded > 0 && (
                        <span className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 border border-amber-300">
                          Balustrade ({balustradesNeeded}x)
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Selected Products List */}
              {selectedProducts.length > 0 && (
                <div className="mt-4">
                  <div className="flex justify-between items-center mb-2">
                    <Label className="text-sm font-medium">Selected Products ({selectedProducts.length})</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearSelection}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Clear All
                    </Button>
                  </div>
                  <div className="space-y-1 max-h-[200px] overflow-y-auto">
                    {selectedProducts.map((product, index) => (
                      <div 
                        key={product.instanceId}
                        className="flex items-center justify-between p-2 bg-white border rounded text-sm"
                      >
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded text-white text-xs flex items-center justify-center" style={{ backgroundColor: product.color }}>
                            {index + 1}
                          </span>
                          <span>{product.name}</span>
                          <span className="text-slate-400">({product.width}mm)</span>
                        </div>
                        <button
                          onClick={() => handleRemoveProduct(product.instanceId)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between mt-6">
          <Button variant="outline" onClick={handleClearSelection} disabled={selectedProducts.length === 0}>
            <Trash2 className="h-4 w-4 mr-2" />
            Clear Selection
          </Button>
          <Button 
            className="bg-orange-600 hover:bg-orange-700"
            onClick={handleSendToZKTeco}
            disabled={sending || selectedProducts.length === 0}
          >
            <Send className="h-4 w-4 mr-2" />
            {sending ? "Sending..." : "Send to ZKTeco for Pricing"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AmazonHardeningProject;
